import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Avatar } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';
import PropTypes from 'prop-types';
import { useState } from 'react';

const SimpleDialog = ({ open, onClose, onAccept, title, description, type = 'info', hideCancel = false }) => {
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleAccept = async () => {
        setIsSubmitted(true);
        try {
            const result = await onAccept();
            return result;
        } finally {
            setIsSubmitted(false); // always reset, no matter what
        }
    };

    const getIconConfig = () => {
        switch (type) {
            case 'error':
                return { icon: <CloseIcon />, bgColor: '#F44336' }; // Red
            case 'warning':
                return { icon: <WarningAmberIcon />, bgColor: '#FF9800' }; // Orange
            default:
                return { icon: <InfoIcon />, bgColor: '#2196F3' }; // Blue
        }
    };

    const { icon, bgColor } = getIconConfig();

    return (
        <Dialog open={open} onClose={onClose} sx={{ '& .MuiPaper-root': { borderRadius: 3, minWidth: 350 } }}>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: bgColor, width: 48, height: 48 }}>{icon}</Avatar>
                    <Typography variant="h3" fontWeight="bold">
                        {title}
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1" sx={{ color: 'text.secondary', mt: 1 }}>
                    {description}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ pb: 2, px: 3 }}>
                <Button
                    disabled={isSubmitted}
                    onClick={handleAccept}
                    variant="contained"
                    sx={{
                        bgcolor: '#4CAF50',
                        '&:hover': { bgcolor: '#388E3C' }
                    }}
                >
                    Proceed
                </Button>
                {!hideCancel && (
                    <Button onClick={onClose} variant="outlined" sx={{ color: 'text.primary', borderColor: 'grey.400' }}>
                        Cancel
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

SimpleDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onAccept: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['info', 'warning', 'error']),
    hideCancel: PropTypes.bool
};

export default SimpleDialog;
